import React, { useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import { Flex, Title } from '@cognite/cogs.js';
import { Spinner, RuleRunsCount } from 'components';
import { compareISODates } from 'utils/misc';
import {
  RuleRunStatus,
  RunStatus,
  SchemaCollection,
  useGetRuleRunStatusQuery,
} from 'types/dt-graphql-apollo-v2';
import { sortRunsByDate } from 'utils/data-validation';
import { isFailedRun } from 'utils/runStatistics';
import { ViewRunDetails } from './ViewRunsPage/ViewRunDetails';
import { ViewRunsPage } from './ViewRunsPage/ViewRunsPage';

export const DataValidation = ({ schema }: { schema: SchemaCollection }) => {
  const {
    data: dataRuns,
    loading,
    startPolling: startPollingRuns,
    stopPolling: stopPollingRuns,
  } = useGetRuleRunStatusQuery({
    variables: {
      schemaId: schema.externalId,
    },
  });

  const allRuns = dataRuns?.listRuleRunStatus?.items as RuleRunStatus[];
  const unsortedRuns = allRuns
    ? allRuns
        ?.filter((run) => run && run.rule && run.rule?.name?.trim())
        .map(isFailedRun)
    : ([] as RuleRunStatus[]);
  const runs = sortRunsByDate(unsortedRuns);

  useEffect(() => {
    const hasIncompleteRun = runs.some(
      (run) => run?.status === RunStatus.InProgress
    );
    if (hasIncompleteRun) {
      startPollingRuns(5000);
    }
    return () => {
      stopPollingRuns();
    };
  }, [runs]);
  const [selectedRun, setSelectedRun] = useState<RuleRunStatus | null>(null);

  useEffect(() => {
    // Reset the selected run to the first existing one
    if (runs && runs.length > 0 && !selectedRun && !loading) {
      setSelectedRun(
        runs.find((run) => run.status !== RunStatus.Error) || null
      );
    }
  }, [runs]);

  const getMostRecentRunFromHistory = (currentRun: RuleRunStatus) => {
    if (runs.length === 0) return null;
    // Filter history by rule id and runAt time
    const history = runs.filter(
      (pastRun) =>
        pastRun &&
        pastRun.rule?.externalId === currentRun.rule?.externalId &&
        compareISODates(pastRun.runAt || null, currentRun.runAt || null)
    );
    if (history.length === 0) return null;
    // Get the most recent run based on runAt time
    return history.reduce((a, b) =>
      !compareISODates(a?.runAt || null, b?.runAt || null) ? a : b
    );
  };

  const renderLoader = () => (
    <SpinnerContainer>
      <Spinner />
    </SpinnerContainer>
  );

  const renderContent = () => {
    if (loading) return renderLoader();
    return (
      <ViewRunsPage
        runs={runs}
        selectedRun={selectedRun || undefined}
        setSelectedRun={setSelectedRun}
      />
    );
  };

  return (
    <>
      <StyledRow>
        <StyledCol>
          <StyledRowHeader>
            <Title level={3}>Runs</Title>
            <RuleRunsCount schemaIds={[schema.externalId]} />
          </StyledRowHeader>
          <StyledTabContainer>{renderContent()}</StyledTabContainer>
        </StyledCol>
        {!!selectedRun && (
          <ViewRunDetails
            selectedRun={selectedRun}
            pastRun={getMostRecentRunFromHistory(selectedRun)}
          />
        )}
      </StyledRow>
    </>
  );
};

const StyledRow = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

const StyledRowHeader = styled(Flex)`
  padding: 2rem 2rem 1.5rem;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
`;

const StyledCol = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const Container = styled.div`
  margin: 0 2rem;
`;

const StyledTabContainer = styled(Container)`
  overflow: auto;
  padding-bottom: 1rem;
`;

const SpinnerContainer = styled(Container)`
  min-height: 150px;
  position: relative;
`;
